import { cloneDeep, flatMap, lowerFirst } from "lodash";
import { getEventModelName } from "@/entities/data-model";
import type { Relations } from "@/entities/data-model";
import type {
    EventFieldsGroup,
    FamilyRelation,
    FormMetadataResponse,
    NameFieldGroup,
    OtherFieldGroup,
    RelationshipFieldGroup,
} from "@/entities/form-metadata";
import {
    EventAssertion,
    FormLevelFeatureType,
    GroupType,
    NameAssertion,
    SpecialAssertion,
} from "@/entities/form-metadata";
import type { Person } from "@/entities/tree-person";
import { LifeEventType, Relationship } from "@/entities/tree-person";

const FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING = {
    [Relationship.Father]: "father",
    [Relationship.Mother]: "mother",
    [Relationship.Spouse]: "spouse",
    [Relationship.Sibling]: "sibling",
    [Relationship.Child]: "child",
} as const;

const cleanFamilyMemberInstances = (currentFamilyMembers: Relations, familyMembers: Person["FamilyMembers"]) => {
    familyMembers.forEach((familyMember) => {
        if (FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]) {
            currentFamilyMembers[FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]].instances = [];
        }
    });
};

const isEmptyFamilyMember = (familyMember: Person["FamilyMembers"][number]) =>
    !familyMember.GivenName && !familyMember.Surname;

const populateFamilyMemberInstances = (
    currentFamilyMembers: Relations,
    familyMembers: Person["FamilyMembers"],
    allowedFamilyMembersObjects: Map<ReturnType<typeof lowerFirst<FamilyRelation>>, Set<NameAssertion>>,
    maxInstancesMap: Map<ReturnType<typeof lowerFirst<FamilyRelation>>, number>,
) => {
    familyMembers.forEach((familyMember) => {
        if (
            FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship] &&
            allowedFamilyMembersObjects.has(FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]) &&
            !isEmptyFamilyMember(familyMember)
        ) {
            const key = FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship];
            const max = maxInstancesMap.get(key) ?? Infinity;
            if (currentFamilyMembers[key].instances.length >= max) return;

            const allowedFamilyMemberFieldsObject = allowedFamilyMembersObjects.get(key);
            const isGivenNameAllowed = allowedFamilyMemberFieldsObject?.has(NameAssertion.GivenName);
            const isSurnameAllowed = allowedFamilyMemberFieldsObject?.has(NameAssertion.Surname);
            // @ts-ignore FIXME
            currentFamilyMembers[key].instances.push({
                givenName: isGivenNameAllowed ? familyMember.GivenName : "",
                surname: isSurnameAllowed ? familyMember.Surname : "",
                secondSurname: "",
                surnameExactness: {
                    flags: {
                        phonetic: false,
                        similar: false,
                        soundex: false,
                    },
                    isExact: false,
                },
                givenNameExactness: {
                    flags: {
                        phonetic: false,
                        similar: false,
                        initials: false,
                    },
                    isExact: false,
                },
            });
        }
    });
};

const obtainFamilyMembersGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) =>
            "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.FamilyMember,
    ) as RelationshipFieldGroup[];
const obtainAllowedFamilyMemberFields = (familyMembersGroups: RelationshipFieldGroup[]) => {
    const allowedFamilyMembersObjects = new Map<ReturnType<typeof lowerFirst<FamilyRelation>>, Set<NameAssertion>>();
    const familyMember = flatMap(familyMembersGroups, (familyMembersGroup) => familyMembersGroup.fieldsets);
    familyMember.forEach((relationship) => {
        const currentStateOfSet = allowedFamilyMembersObjects.get(lowerFirst(relationship.relation)) ?? new Set();
        const unwrappedFamilyMemberFields = new Set(flatMap(relationship.fields, (field) => field.assertion));
        allowedFamilyMembersObjects.set(
            lowerFirst(relationship.relation),
            new Set([...Array.from(currentStateOfSet), ...Array.from(unwrappedFamilyMemberFields)]),
        );
    });

    return allowedFamilyMembersObjects;
};

const obtainMaxFamilyMemberInstances = (familyMembersGroups: RelationshipFieldGroup[], currentFamily: Relations) => {
    const maxInstancesMap = new Map<ReturnType<typeof lowerFirst<FamilyRelation>>, number>();
    familyMembersGroups.forEach((group) => {
        if (group.fieldsetDisplayStrategy !== "prefilled-static") return;
        group.fieldsets.forEach((fieldset) => {
            const key = lowerFirst(fieldset.relation) as keyof Relations;
            if (currentFamily[key]) {
                maxInstancesMap.set(key, currentFamily[key].instances.length);
            }
        });
    });
    return maxInstancesMap;
};

export const getFamilyMemberDetails = <T extends { family: Relations }>(
    familyMembers: Person["FamilyMembers"],
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
): T["family"] => {
    const familyMembersGroups = obtainFamilyMembersGroups(formMetadata);
    const allowedFamilyMembersObjects = obtainAllowedFamilyMemberFields(familyMembersGroups);
    const maxInstancesMap = obtainMaxFamilyMemberInstances(familyMembersGroups, currentFormValue.family);

    const currentFamilyMembers = cloneDeep(currentFormValue.family);
    cleanFamilyMemberInstances(currentFamilyMembers, familyMembers);
    populateFamilyMemberInstances(currentFamilyMembers, familyMembers, allowedFamilyMembersObjects, maxInstancesMap);

    const dynamicRelations = new Set<keyof Relations>();
    familyMembersGroups.forEach((group) => {
        if (group.fieldsetDisplayStrategy === "prefilled-dynamic") {
            group.fieldsets.forEach((fieldset) => {
                dynamicRelations.add(lowerFirst(fieldset.relation) as keyof Relations);
            });
        }
    });

    for (const key of dynamicRelations) {
        currentFamilyMembers[key].instances = currentFamilyMembers[key].instances.filter(
            (instance) => instance.givenName || instance.surname,
        );
    }

    return currentFamilyMembers;
};

const EVENT_TO_DETAILS_MAPPING = {
    [LifeEventType.Birth]: "SelfBirth",
    [LifeEventType.Death]: "SelfDeath",
    [LifeEventType.Marriage]: "SelfMarriage",
    [LifeEventType.Residence]: "SelfResidence",
    [LifeEventType.Any]: "Self",
} as const;

const cleanEventInstances = <T extends Record<string, { instances: any[] }>>(
    currentEvents: T,
    lifeEvents: Person["LifeEvents"],
) => {
    lifeEvents.forEach((lifeEvent) => {
        if (EVENT_TO_DETAILS_MAPPING[lifeEvent.Type] && currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]]) {
            currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]]!.instances = [];
        }
    });
};

const isEmptyEvent = (lifeEvent: Person["LifeEvents"][number]) =>
    lifeEvent.Date.Year == null &&
    lifeEvent.Date.Month == null &&
    lifeEvent.Date.Day == null &&
    !lifeEvent.LocationText;

const SINGLETON_EVENT_TYPES: ReadonlySet<string> = new Set([
    LifeEventType.Birth,
    LifeEventType.Death,
    LifeEventType.Marriage,
]);

const shouldSkipEvent = (lifeEvent: Person["LifeEvents"][number]) =>
    isEmptyEvent(lifeEvent) || (SINGLETON_EVENT_TYPES.has(lifeEvent.Type) && lifeEvent.Date.Year == null);

const populateEventInstances = <T extends Record<string, { instances: any[] }>>(
    currentEvents: T,
    lifeEvents: Person["LifeEvents"],
    allowedEventObjects: Map<ReturnType<typeof getEventModelName>, Set<EventAssertion>>,
    maxInstancesMap: Map<ReturnType<typeof getEventModelName>, number>,
) => {
    lifeEvents.forEach((lifeEvent) => {
        if (
            EVENT_TO_DETAILS_MAPPING[lifeEvent.Type] &&
            allowedEventObjects.has(EVENT_TO_DETAILS_MAPPING[lifeEvent.Type] as ReturnType<typeof getEventModelName>) &&
            currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]] &&
            !shouldSkipEvent(lifeEvent)
        ) {
            const eventKey = EVENT_TO_DETAILS_MAPPING[lifeEvent.Type] as ReturnType<typeof getEventModelName>;
            const max = maxInstancesMap.get(eventKey) ?? Infinity;
            if (currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]]!.instances.length >= max) return;

            const allowedEventFieldsObject = allowedEventObjects.get(eventKey);
            const isDayAllowed = allowedEventFieldsObject?.has(EventAssertion.Day);
            const isMonthAllowed = allowedEventFieldsObject?.has(EventAssertion.Month);
            const isYearAllowed = allowedEventFieldsObject?.has(EventAssertion.Year);
            const isPlaceAllowed = allowedEventFieldsObject?.has(EventAssertion.Place);
            currentEvents[EVENT_TO_DETAILS_MAPPING[lifeEvent.Type]]!.instances.push({
                date: {
                    year: isYearAllowed ? lifeEvent.Date.Year : null,
                    month: isMonthAllowed ? lifeEvent.Date.Month : null,
                    day: isDayAllowed ? lifeEvent.Date.Day : null,
                },
                dateProximity: {
                    year: null,
                    month: null,
                    day: null,
                },
                gpid: lifeEvent.LocationGpids?.Gpid,
                location: isPlaceAllowed ? lifeEvent.LocationText : "",
                locationExactness: null,
            });
        }
    });
};

const obtainEventGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) =>
            "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.Event,
    ) as EventFieldsGroup[];
const obtainAllowedEventFields = (eventGroups: EventFieldsGroup[]) => {
    const allowedEventObjects = new Map<ReturnType<typeof getEventModelName>, Set<EventAssertion>>();
    const fieldsets = flatMap(eventGroups, (eventGroup) => eventGroup.fieldsets);

    fieldsets.forEach((fieldset) => {
        const currentStateOfSet =
            allowedEventObjects.get(
                getEventModelName({ eventType: fieldset.eventType, relation: fieldset.relation }),
            ) ?? new Set();
        const unwrappedEventFields = new Set(flatMap(fieldset.fields, (field) => field.assertion));

        allowedEventObjects.set(
            getEventModelName({ eventType: fieldset.eventType, relation: fieldset.relation }),
            new Set([...Array.from(currentStateOfSet), ...Array.from(unwrappedEventFields)]),
        );
    });

    return allowedEventObjects;
};

const obtainMaxEventInstances = (
    eventGroups: EventFieldsGroup[],
    currentEvents: Record<string, { instances: any[] }>,
) => {
    const maxInstancesMap = new Map<ReturnType<typeof getEventModelName>, number>();
    eventGroups.forEach((eventGroup) => {
        if (eventGroup.fieldsetDisplayStrategy !== "prefilled-static") return;
        eventGroup.fieldsets.forEach((fieldset) => {
            const key = getEventModelName({ eventType: fieldset.eventType, relation: fieldset.relation });
            if (key && currentEvents[key]) {
                maxInstancesMap.set(key, currentEvents[key].instances.length);
            }
        });
    });
    return maxInstancesMap;
};

const prepareLifeEvents = (lifeEvents: Person["LifeEvents"], formMetadata: FormMetadataResponse) => {
    if (formMetadata.contentType !== "collection") return lifeEvents;

    const nonResidence: Person["LifeEvents"] = [];
    const residences: Person["LifeEvents"] = [];

    for (const event of lifeEvents) {
        if (event.Type === LifeEventType.Residence) {
            if (event.Date.Year != null && !!event.LocationText) {
                residences.push(event);
            }
        } else {
            nonResidence.push(event);
        }
    }

    residences.sort((a, b) => a.Date.Year! - b.Date.Year!);

    return [...nonResidence, ...residences];
};

export const getEventsDetails = <T extends { events: Record<string, { instances: any[] }> }>(
    lifeEvents: Person["LifeEvents"],
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
): T["events"] => {
    const eventGroups = obtainEventGroups(formMetadata);
    const allowedEventObjects = obtainAllowedEventFields(eventGroups);
    const maxInstancesMap = obtainMaxEventInstances(eventGroups, currentFormValue.events);

    const preparedEvents = prepareLifeEvents(lifeEvents, formMetadata);
    const currentEvents = cloneDeep(currentFormValue.events);
    cleanEventInstances(currentEvents, preparedEvents);
    populateEventInstances(currentEvents, preparedEvents, allowedEventObjects, maxInstancesMap);

    if (formMetadata.contentType === "collection" && currentEvents["SelfResidence"]) {
        for (const instance of currentEvents["SelfResidence"].instances) {
            instance.date = { year: null, month: null, day: null };
        }
    }

    const dynamicEventKeys = new Set<string>();
    eventGroups.forEach((group) => {
        if (group.fieldsetDisplayStrategy === "prefilled-dynamic") {
            group.fieldsets.forEach((fieldset) => {
                const key = getEventModelName({ eventType: fieldset.eventType, relation: fieldset.relation });
                if (key) dynamicEventKeys.add(key);
            });
        }
    });

    for (const key of dynamicEventKeys) {
        if (currentEvents[key]) {
            currentEvents[key].instances = currentEvents[key].instances.filter(
                (instance: any) =>
                    instance.date?.year != null ||
                    instance.date?.month != null ||
                    instance.date?.day != null ||
                    instance.location,
            );
        }
    }

    return currentEvents;
};

const obtainNameGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) => "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === "name",
    );
const obtainAllowedNameFields = (nameGroups: NameFieldGroup[]) => {
    const foundAssertions = new Set<NameAssertion>();
    nameGroups.forEach((nameGroup) => {
        nameGroup.fields.forEach((field) => {
            foundAssertions.add(field.assertion);
        });
    });
    return foundAssertions;
};

export const getNameDetails = <T extends { name: any }>(
    person: Person,
    formMetadata: FormMetadataResponse,
): T["name"] => {
    const nameGroups = obtainNameGroups(formMetadata);
    const foundAssertions = obtainAllowedNameFields(nameGroups);

    return {
        givenName: foundAssertions.has(NameAssertion.GivenName) ? person.GivenName : "",
        surname: foundAssertions.has(NameAssertion.Surname) ? person.FatherSurname || person.Surname : "",
        secondSurname: foundAssertions.has(NameAssertion.SecondSurname) ? person.MotherSurname : "",
        givenNameExactness: {
            flags: {
                phonetic: false,
                similar: false,
                initials: false,
            },
            isExact: false,
        },
        surnameExactness: {
            flags: {
                phonetic: false,
                similar: false,
                soundex: false,
            },
            isExact: false,
        },
    };
};

const GENDER_FIELD_KEY = "SelfGender";

const hasGenderField = (formMetadata: FormMetadataResponse) => {
    for (const fieldGroupOrFeature of formMetadata.fieldGroupsAndFeatureContainers) {
        const isOtherGroup = "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.Other;

        if (isOtherGroup) {
            const otherGroup = fieldGroupOrFeature as OtherFieldGroup;
            const hasGender = otherGroup.others.some(
                (other) => other.assertion === SpecialAssertion.Gender && other.relation === "Self",
            );
            if (hasGender) return true;
        }
    }

    return false;
};

export const getGenderDetails = <T extends { fields: Record<string, any> }>(
    person: Person,
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
): T["fields"] => {
    const isGenderFieldAllowed = hasGenderField(formMetadata);

    const currentFields = structuredClone(currentFormValue.fields);

    if (isGenderFieldAllowed) {
        currentFields[GENDER_FIELD_KEY] = {
            ...currentFields[GENDER_FIELD_KEY],
            value: person.Gender ?? "",
        };
    }

    return currentFields;
};

export const getTimespanDetails = <
    T extends {
        timespan: {
            isVisible: boolean;
            yearStart: number | null;
            yearEnd: number | null;
            matchMode: "rankOnly" | "mustMatch";
        };
    },
>(
    baseFormValues: T,
    formMetadata: FormMetadataResponse,
    currentFormValues: T,
): T["timespan"] => {
    const timespanFeature = formMetadata.features.find((f) => f.featureType === FormLevelFeatureType.Timespan);
    let timespan = structuredClone(baseFormValues.timespan);
    if (timespanFeature) {
        const hadTimespan = currentFormValues.timespan.isVisible;
        if (!hadTimespan) {
            timespan.isVisible = false;
        } else {
            timespan = {
                yearStart: timespanFeature.attributes.sliderRange.min,
                yearEnd: timespanFeature.attributes.sliderRange.max,
                matchMode: "rankOnly",
                isVisible: true,
            };
        }
    }

    return timespan;
};
